import { Capacitor } from "@capacitor/core";

/**
 * LIVORA LAUNCH PHASE 1 — single source of truth for the call-room route.
 *
 * Root cause this replaces: callers/receivers previously decided
 * /call-native/:callId vs /call/:callId from two build-time env flags
 * (REACT_APP_NATIVE_CALL_ROOM_TEST / REACT_APP_NATIVE_CALL_ROOM_ROUTING_TEST)
 * with no actual platform check at all, so a real Android Capacitor build
 * without both flags set always landed in the Web call room. This helper
 * decides purely from Capacitor platform detection - never userAgent, never
 * an env flag, never caller/receiver or source-page differences.
 */
export function isAndroidNativeApp() {
  try {
    return Capacitor.isNativePlatform() === true && Capacitor.getPlatform() === "android";
  } catch {
    return false;
  }
}

/**
 * Phase 1A: reject the values that would otherwise produce a malformed
 * route such as /call-native/undefined, /call-native/null, or
 * /call-native/ (empty). Guards both directions - never just the outbound
 * navigate() call, so a bad callId can never even be constructed.
 */
export function isValidRouteCallId(callId) {
  if (callId == null) return false;
  const id = String(callId).trim();
  if (!id) return false;
  if (id === "undefined" || id === "null") return false;
  return true;
}

/**
 * Resolve the call-room route for a given callId. Always the same decision
 * regardless of role (caller/receiver) or the page initiating navigation.
 * Never constructs /call-native/undefined, /call-native/null, or
 * /call-native/ - an invalid callId falls back to /home instead.
 */
export function resolveCallRoomRoute({ callId, role, source } = {}) {
  const android = isAndroidNativeApp();
  if (!isValidRouteCallId(callId)) {
    console.log(
      `[callRoute] invalid_call_id role=${role || "unknown"} source=${source || "unknown"} callId=${JSON.stringify(callId ?? null)}`
    );
    return { target: "/home", isAndroidNative: android, invalidCallId: true };
  }
  const id = String(callId).trim();
  const target = android ? `/call-native/${id}` : `/call/${id}`;
  console.log(
    `[callRoute] platform=${android ? "android" : "web"} target=${android ? "native" : "web"} role=${role || "unknown"} source=${source || "unknown"} callId=${id}`
  );
  return { target, isAndroidNative: android, invalidCallId: false };
}

export function logNativeCallOpenSuccess({ callId } = {}) {
  console.log(`[callRoute] native_open_success callId=${String(callId || "")}`);
}

export function logNativeCallOpenFailed({ callId, reason } = {}) {
  console.log(
    `[callRoute] native_open_failed callId=${String(callId || "")} reason=${String(reason || "unknown")}`
  );
}

export function logAndroidWebRouteBlocked({ callId, source } = {}) {
  console.log(
    `[callRoute] android_web_route_blocked callId=${String(callId || "")} source=${String(source || "unknown")}`
  );
}

export function logAndroidWebMountPrevented({ callId, reason } = {}) {
  console.log(
    `[callRoute] android_web_mount_prevented callId=${String(callId || "")} reason=${String(reason || "unknown")}`
  );
}

export function logAndroidNativeRedirectFailed({ callId, reason } = {}) {
  console.log(
    `[callRoute] android_native_redirect_failed callId=${String(callId || "")} reason=${String(reason || "unknown")}`
  );
}

export function logBrowserNativeRouteBlocked({ callId, source } = {}) {
  console.log(
    `[callRoute] browser_native_route_blocked callId=${String(callId || "")} source=${String(source || "unknown")}`
  );
}
